/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { Fragment, useMemo, useEffect, useLayoutEffect, useRef, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

// Action-button columns move to a footer strip in the mobile card layout.
const isActionColumn = (column) => /^actions?$/i.test(String(column.id || ""));

// Email columns may break inside the address (see DataTableBodyCell); a column can also opt in or
// out explicitly with `breakAnywhere: true/false`.
const isEmailColumn = (column) => /email/i.test(String(column.id || ""));

// Columns whose Header is empty (e.g. an avatar or checkbox column) render full width, unlabelled.
const hasHeaderText = (column) =>
  typeof column.Header === "string" ? column.Header.trim() !== "" : Boolean(column.Header);

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  renderRowDetail,
  mobileLayout,
  resetPageKey,
}) {
  // Below md (768px) rows render as stacked cards of label/value pairs instead of a wide table.
  // Built from the same columns and Cell renderers, so no page needs its own mobile markup.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("md"));
  const showCards = mobileLayout === "cards" && isPhone;

  // When a wide table still scrolls sideways inside its card, pin the actions column to the
  // right edge so the row buttons never need scrolling to. Tables that fit are left as they are.
  const containerRef = useRef(null);
  const [tableOverflows, setTableOverflows] = useState(false);
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    // Auto resets off: pages build `table` inline, so any re-render of the page looked like
    // new data and sent the user back to page 1 (and cleared sort/search). Changing the search or
    // sort goes back to page 1 and shrinking data steps back a page, both handled below.
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetGlobalFilter: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter, sortBy },
  } = tableInstance;

  // If the data shrinks (e.g. a row is deleted) so the current page no longer exists, step back.
  // Declared before the reset below so a reset to page 1 in the same render wins over this.
  useLayoutEffect(() => {
    const last = Math.max(pageOptions.length - 1, 0);
    if (pageIndex > last) gotoPage(last);
  }, [pageOptions.length, pageIndex]);

  // Back to page 1 when the table's search or sort changes, or when the page's own filters
  // (passed as resetPageKey) change. Layout effects so there is no frame showing a stale page.
  const resetMounted = useRef(false);
  useLayoutEffect(() => {
    if (resetMounted.current) gotoPage(0);
    resetMounted.current = true;
  }, [globalFilter, sortBy, resetPageKey]);

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || showCards || typeof ResizeObserver === "undefined") {
      setTableOverflows(false);
      return undefined;
    }
    const check = () => setTableOverflows(el.scrollWidth > el.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    const tableEl = el.querySelector("table");
    if (tableEl) observer.observe(tableEl);
    return () => observer.disconnect();
  }, [showCards, page]);

  // Setting the entries starting point
  const entriesStart = rows.length === 0 ? 0 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  const entriesEnd = Math.min(pageSize * (pageIndex + 1), rows.length);

  return (
    <TableContainer ref={containerRef} sx={{ boxShadow: "none" }}>
      {entriesPerPage || canSearch ? (
        <MDBox
          display="flex"
          flexWrap="wrap"
          gap={1.5}
          justifyContent="space-between"
          alignItems="center"
          p={{ xs: 2, md: 3 }}
        >
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox ml="auto" sx={{ flex: { xs: "1 1 100%", sm: "0 0 12rem" } }}>
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(search);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      {showCards ? (
        <MDBox px={{ xs: 1.5, sm: 2 }} pb={1} display="flex" flexDirection="column" gap={1.5}>
          {page.map((row, key) => {
            prepareRow(row);
            const detail = renderRowDetail ? renderRowDetail(row.original) : null;
            const fieldCells = row.cells.filter((cell) => !isActionColumn(cell.column));
            const actionCells = row.cells.filter((cell) => isActionColumn(cell.column));
            return (
              <MDBox
                key={key}
                role="row"
                sx={({ palette: { light }, borders: { borderWidth, borderRadius } }) => ({
                  border: `${borderWidth[1]} solid ${light.main}`,
                  borderRadius: borderRadius.lg,
                  px: 1.5,
                  py: 1,
                })}
              >
                {fieldCells.map((cell, idx) => {
                  const labelled = hasHeaderText(cell.column);
                  return (
                    <MDBox
                      key={idx}
                      role="cell"
                      display="grid"
                      gridTemplateColumns={
                        labelled ? "minmax(84px, 34%) minmax(0, 1fr)" : "minmax(0, 1fr)"
                      }
                      columnGap={1.5}
                      alignItems="center"
                      py={0.75}
                      sx={({ palette: { light }, borders: { borderWidth } }) => ({
                        borderBottom:
                          idx < fieldCells.length - 1
                            ? `${borderWidth[1]} solid ${light.main}`
                            : "none",
                      })}
                    >
                      {labelled && (
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color="secondary"
                          textTransform="uppercase"
                          sx={{ fontSize: "0.65rem", letterSpacing: "0.04em", lineHeight: 1.3 }}
                        >
                          {cell.column.render("Header")}
                        </MDTypography>
                      )}
                      <MDBox
                        minWidth={0}
                        sx={({ typography: { size } }) => ({
                          fontSize: size.sm,
                          overflowWrap: "anywhere",
                        })}
                      >
                        {cell.render("Cell")}
                      </MDBox>
                    </MDBox>
                  );
                })}
                {detail && (
                  <MDBox pt={0.5} pb={0.75}>
                    {detail}
                  </MDBox>
                )}
                {actionCells.length > 0 && (
                  <MDBox
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={1}
                    pt={1}
                    mt={0.25}
                    sx={({ palette: { light }, borders: { borderWidth } }) => ({
                      borderTop: `${borderWidth[1]} solid ${light.main}`,
                    })}
                  >
                    {actionCells.map((cell, idx) => (
                      <Fragment key={idx}>{cell.render("Cell")}</Fragment>
                    ))}
                  </MDBox>
                )}
              </MDBox>
            );
          })}
        </MDBox>
      ) : (
        <Table {...getTableProps()}>
          <MDBox component="thead">
            {headerGroups.map((headerGroup, key) => (
              <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, idx) => (
                  <DataTableHeadCell
                    key={idx}
                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                    width={column.width ? column.width : "auto"}
                    align={column.align ? column.align : "left"}
                    sorted={setSortedValue(column)}
                    sticky={tableOverflows && isActionColumn(column)}
                  >
                    {column.render("Header")}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </MDBox>
          <TableBody {...getTableBodyProps()}>
            {page.map((row, key) => {
              prepareRow(row);
              const isEndRow = noEndBorder && rows.length - 1 === key;
              // Optional full-width line under the row (e.g. a long description) — body
              // cells are width:max-content, so long text in a column cell never wraps.
              const detail = renderRowDetail ? renderRowDetail(row.original) : null;
              return (
                <Fragment key={key}>
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map((cell, idx) => (
                      <DataTableBodyCell
                        key={idx}
                        noBorder={Boolean(detail) || isEndRow}
                        align={cell.column.align ? cell.column.align : "left"}
                        breakAnywhere={cell.column.breakAnywhere ?? isEmailColumn(cell.column)}
                        sticky={tableOverflows && isActionColumn(cell.column)}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </DataTableBodyCell>
                    ))}
                  </TableRow>
                  {detail && (
                    <TableRow>
                      <MDBox
                        component="td"
                        colSpan={row.cells.length}
                        pt={0}
                        pb={1.5}
                        px={3}
                        sx={({ palette: { light }, borders: { borderWidth } }) => ({
                          borderBottom: isEndRow ? "none" : `${borderWidth[1]} solid ${light.main}`,
                        })}
                      >
                        {detail}
                      </MDBox>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : { xs: 2, md: 3 }}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </MDTypography>
          </MDBox>
        )}
        {pageOptions.length > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {canPreviousPage && (
              <MDPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                  value={customizedPageOptions[pageIndex]}
                  onChange={(handleInputPagination, handleInputPaginationValue)}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <MDPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  renderRowDetail: null,
  mobileLayout: "cards",
  resetPageKey: "",
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  renderRowDetail: PropTypes.func,
  // "cards" (default): rows become label/value cards below 768px. "table": always a table
  // (it scrolls sideways), for tables that only make sense as a grid.
  mobileLayout: PropTypes.oneOf(["cards", "table"]),
  // Changes when the page filters the rows from outside (its own search/status filters): back to page 1
  resetPageKey: PropTypes.string,
};

export default DataTable;
